/**
 * UpscaleMode V2
 *
 * Bild-Hochskalierung mit:
 * - Upload oder Workspace-Auswahl (einzelnes Bild)
 * - Verschiedene KI-Modelle
 * - Skalierungsfaktor-Slider
 * - Optionales Schärfen
 *
 * @accessibility WCAG 2.1 AA + BFSG konform
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ZoomIn, Check, Image as ImageIcon, Zap, Star } from 'lucide-react';
import { Button, Card, CardContent, CardHeader, CardTitle, Checkbox, EmptyState, Loading } from '@/components/v2/ui';
import { InputSourceToggle, UploadArea, ResultPreview, type InputSource } from '@/components/v2/tools/shared';
import { ImageGallery } from '@/components/v2/tools/gallery';
import { useWorkspaceImages } from '@/hooks/useWorkspaceImages';
import {
  upscaleImageUpload,
  type UpscaleParams,
  type UploadOperationResult,
} from '@/lib/api/imageOperations';
import { cn } from '@/lib/utils';

type ProcessingState = 'idle' | 'processing' | 'done';
type Model = NonNullable<UpscaleParams['model']>;

interface ModelInfo {
  value: Model;
  label: string;
  speed: number; // 1-5
  quality: number; // 1-5
  descriptionKey: string;
}

const MODELS: ModelInfo[] = [
  {
    value: 'espcn',
    label: 'ESPCN',
    speed: 5,
    quality: 3,
    descriptionKey: 'editor.modelEspcnDesc',
  },
  {
    value: 'fsrcnn',
    label: 'FSRCNN',
    speed: 5,
    quality: 3,
    descriptionKey: 'editor.modelFsrcnnDesc',
  },
  {
    value: 'lapsrn',
    label: 'LapSRN',
    speed: 3,
    quality: 4,
    descriptionKey: 'editor.modelLapsrnDesc',
  },
  {
    value: 'edsr',
    label: 'EDSR',
    speed: 1,
    quality: 5,
    descriptionKey: 'editor.modelEdsrDesc',
  },
  {
    value: 'lanczos',
    label: 'Lanczos',
    speed: 5,
    quality: 2,
    descriptionKey: 'editor.modelLanczosDesc',
  },
];

export function UpscaleMode() {
  const { t } = useTranslation();
  // Input State
  const [inputSource, setInputSource] = useState<InputSource>('upload');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Settings State
  const [model, setModel] = useState<Model>('espcn');
  const [scaleFactor, setScaleFactor] = useState(2.0);
  const [applySharpening, setApplySharpening] = useState(true);

  // Processing State
  const [state, setState] = useState<ProcessingState>('idle');
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<UploadOperationResult | null>(null);

  // Workspace Images Query
  const { images, pagination, isLoading, isFetching } = useWorkspaceImages({
    page: currentPage,
    perPage: 12,
    enabled: inputSource === 'workspace',
  });

  // Handlers
  const handleSourceChange = (source: InputSource) => {
    setInputSource(source);
    setUploadedFile(null);
    setSelectedAssetId(null);
    setResult(null);
    setState('idle');
  };

  const handleFilesChange = (files: File[]) => {
    setUploadedFile(files[0] || null);
    setResult(null);
    setState('idle');
  };

  const handleSelectionChange = useCallback((ids: Set<string>) => {
    const firstId = Array.from(ids)[0] || null;
    setSelectedAssetId(firstId);
    setResult(null);
    setState('idle');
  }, []);

  const handleProcess = async () => {
    setState('processing');
    setProgress(0);
    setResult(null);

    try {
      let fileToProcess: File | null = null;

      if (inputSource === 'upload') {
        fileToProcess = uploadedFile;
      } else if (selectedAssetId) {
        // Download workspace image as file
        const response = await fetch(`/image/${selectedAssetId}`);
        const blob = await response.blob();
        fileToProcess = new File([blob], `image_${selectedAssetId}.png`, { type: blob.type });
      }

      if (!fileToProcess) {
        throw new Error(t('editor.noImageSelected'));
      }

      // Simulate progress based on model speed
      const selectedModel = MODELS.find((m) => m.value === model);
      const progressSpeed = selectedModel ? selectedModel.speed * 3 : 10;

      const progressInterval = setInterval(() => {
        setProgress((prev) => Math.min(prev + Math.random() * progressSpeed, 90));
      }, 400);

      const response = await upscaleImageUpload(fileToProcess, {
        scale_factor: scaleFactor,
        model,
        apply_sharpening: applySharpening,
      });

      clearInterval(progressInterval);
      setProgress(100);

      if (response.success) {
        setResult(response);
        setState('done');
        toast.success(t('toast.success.imageProcessed'));
      } else {
        throw new Error(response.error || t('editor.processingFailed'));
      }
    } catch (error) {
      setState('idle');
      setProgress(0);
      toast.error(error instanceof Error ? error.message : t('editor.processingFailed'));
    }
  };

  const handleReset = () => {
    setState('idle');
    setProgress(0);
    setResult(null);
    setUploadedFile(null);
    setSelectedAssetId(null);
  };

  const hasInput = inputSource === 'upload' ? !!uploadedFile : !!selectedAssetId;
  const isProcessing = state === 'processing';
  const canProcess = hasInput && state === 'idle';

  const renderRating = (value: number, icon: React.ElementType, color: string) => {
    const Icon = icon;
    return (
      <div className="flex items-center gap-0.5">
        {[...Array(5)].map((_, i) => (
          <Icon
            key={i}
            className={cn(
              'h-3 w-3',
              i < value ? color : 'text-neutral-300 dark:text-neutral-600'
            )}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/30">
          <ZoomIn className="h-6 w-6 text-orange-600 dark:text-orange-400" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-neutral-900 dark:text-white">
            {t('editor.upscaleTitle')}
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            {t('editor.upscaleDescription')}
          </p>
        </div>
      </div>

      {state === 'done' && result ? (
        // Result View
        <ResultPreview
          assetId={result.image_id || ''}
          imageUrl={result.image_url}
          message={t('editor.upscaleResult', { factor: result.scale_factor || scaleFactor })}
          originalDimensions={result.original_dimensions}
          newDimensions={result.new_dimensions}
          processingTimeMs={result.processing_time_ms}
          onReset={handleReset}
        />
      ) : (
        // Input View
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Input Section */}
          <div className="lg:col-span-2 space-y-4">
            <InputSourceToggle value={inputSource} onChange={handleSourceChange} />

            {inputSource === 'upload' ? (
              <Card>
                <CardContent className="p-4">
                  <UploadArea
                    files={uploadedFile ? [uploadedFile] : []}
                    onFilesChange={handleFilesChange}
                    maxFiles={1}
                    acceptedFormats={['image/png', 'image/jpeg', 'image/webp']}
                    disabled={state === 'processing'}
                  />
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-4">
                  {isLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loading size="lg" />
                    </div>
                  ) : images && images.length > 0 ? (
                    <ImageGallery
                      images={images}
                      isLoading={isLoading}
                      isFetching={isFetching}
                      pagination={pagination}
                      onPageChange={setCurrentPage}
                      selectedIds={selectedAssetId ? new Set([selectedAssetId]) : new Set()}
                      onSelectionChange={handleSelectionChange}
                    />
                  ) : (
                    <EmptyState
                      icon={<ImageIcon className="h-12 w-12" />}
                      title={t('gallery.noImages')}
                      description={t('editor.noWorkspaceImages')}
                    />
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Settings Section */}
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{t('common.settings')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Model Selection */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
                    {t('editor.modelLabel')}
                  </label>
                  <div className="space-y-2">
                    {MODELS.map((m) => (
                      <button
                        key={m.value}
                        onClick={() => setModel(m.value)}
                        disabled={state === 'processing'}
                        className={cn(
                          'w-full p-3 rounded-lg border text-left transition-colors',
                          model === m.value
                            ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                            : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600'
                        )}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-sm">{m.label}</span>
                          {model === m.value && (
                            <Check className="h-4 w-4 text-orange-500" />
                          )}
                        </div>
                        <p className="text-xs text-neutral-500 mb-2">{t(m.descriptionKey)}</p>
                        <div className="flex items-center gap-4 text-xs">
                          <div className="flex items-center gap-1">
                            <span className="text-neutral-400">{t('editor.speedRating')}</span>
                            {renderRating(m.speed, Zap, 'text-amber-500')}
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-neutral-400">{t('editor.qualityRating')}</span>
                            {renderRating(m.quality, Star, 'text-orange-500')}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Scale Factor Slider */}
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <label className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
                      {t('editor.scaleFactor')}
                    </label>
                    <span className="text-sm font-semibold text-orange-500">{scaleFactor}x</span>
                  </div>
                  <input
                    type="range"
                    min="1.5"
                    max="2.0"
                    step="0.1"
                    value={scaleFactor}
                    onChange={(e) => setScaleFactor(Number(e.target.value))}
                    disabled={state === 'processing'}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-neutral-200 dark:bg-neutral-700"
                  />
                  <div className="flex justify-between text-xs text-neutral-500">
                    <span>1.5x</span>
                    <span>2.0x</span>
                  </div>
                </div>

                {/* Sharpening Toggle */}
                <div className="flex items-center justify-between p-3 rounded-lg border border-neutral-200 dark:border-neutral-700">
                  <div>
                    <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
                      {t('editor.sharpen')}
                    </p>
                    <p className="text-xs text-neutral-500">
                      {t('editor.sharpenHint')}
                    </p>
                  </div>
                  <Checkbox
                    checked={applySharpening}
                    onChange={(e) => setApplySharpening(e.target.checked)}
                    disabled={state === 'processing'}
                  />
                </div>

                {/* Progress */}
                {state === 'processing' && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>{t('common.processing')}</span>
                      <span>{Math.round(progress)}%</span>
                    </div>
                    <div className="h-2 bg-neutral-200 dark:bg-neutral-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-orange-500 transition-all duration-300"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Process Button */}
                <Button
                  className="w-full"
                  onClick={handleProcess}
                  disabled={!canProcess || isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      {t('common.processing')}
                    </>
                  ) : (
                    <>
                      <ZoomIn className="h-4 w-4 mr-2" />
                      {hasInput ? t('editor.upscaleButton', { factor: scaleFactor }) : t('editor.selectImageButton')}
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Info Card */}
            <Card className="bg-neutral-50 dark:bg-neutral-800/50">
              <CardContent className="p-4 space-y-2">
                <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
                  {t('editor.hints')}
                </p>
                <ul className="text-xs text-neutral-500 space-y-1">
                  <li>• {t('editor.edserQuality')}</li>
                  <li>• {t('editor.espcnQuality')}</li>
                  <li>• {t('editor.lanczosHint')}</li>
                  <li>• {t('editor.maxFileSize')}</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

export default UpscaleMode;
